import { useQuery } from '@apollo/client'
import { SettingsLayout } from '@components/layouts/settings-layout'
import { BaseBlock } from '@components/base-block'
import { BaseButton } from '@components/base-button'
import { LabelM, LabelXS } from '@components/base-typography'
import { EmptyList } from '@components/empty'
import { Pagination } from '@components/pagination'
import { InquiryStatusTag } from '@components/tag/inquiry-status-tag'
import { withAuthSSR } from '@lib/apollo/with-auth-ssr'
import {
  GetInquiriesByOffsetDocument,
  InquirySortBy,
  MeQuery,
} from '@lib/graphql/documents.generated'
import { usePagination } from '@lib/hooks/use-pagination'
import { Routes } from '@lib/routes'
import { dateFormat } from '@lib/utils/date-format'
import { generatePath } from '@lib/utils/generate-path'
import { colors } from '@styles/colors'
import { borderRadius, transition } from '@styles/utils'
import { NextPage } from 'next'
import Link from 'next/link'
import { padding } from 'polished'
import { useStyletron } from 'styletron-react'

export const getServerSideProps = withAuthSSR((_, user) => async () => {
  return {
    props: {
      user,
    },
  }
})

interface InquiriesSettingsPageProps {
  user: MeQuery['me']
}

export const INQUIRIES_PAGE_LIMIT = 12

const InquiriesSettings: NextPage<InquiriesSettingsPageProps> = ({ user }) => {
  const [css] = useStyletron()

  const { paginationProps, setCountTotal, limit, offset } =
    usePagination(INQUIRIES_PAGE_LIMIT)

  const { data } = useQuery(GetInquiriesByOffsetDocument, {
    variables: {
      offset,
      limit,
      sortBy: InquirySortBy.CreatedAtDesc,
      filter: {
        questionerId: user.id,
      },
    },
    onCompleted: ({ inquiries: { pageInfo } }) => {
      if (pageInfo) {
        setCountTotal(pageInfo.countTotal)
      }
    },
  })

  const inquiries = data?.inquiries.edges || []

  return (
    <BaseBlock>
      <Link href={Routes.SUPPORT_INQUIRY_WRITE} passHref>
        <BaseButton $size="m" $type="tertiary" leading="Edit" spaceBottom={24}>
          문의하기
        </BaseButton>
      </Link>
      {inquiries.map(({ node: { id, createdAt, title, answeredAt } }) => {
        return (
          <Link
            key={id}
            href={generatePath(Routes.SETTINGS_INQUIRIES_DETAIL, {
              id,
            })}
            passHref
          >
            <BaseBlock
              flexbox
              as="a"
              cursor="pointer"
              alignItems="center"
              className={css({
                ...padding('16px', '8px'),
                ...transition('background-color'),
                ...borderRadius(8),
                columnGap: '16px',
                ':hover': {
                  backgroundColor: colors.gray50,
                },
              })}
            >
              <InquiryStatusTag isAnswered={Boolean(answeredAt)} />
              <LabelM>{title}</LabelM>
              <LabelXS marginLeft="auto" color={colors.gray500}>
                {dateFormat(createdAt, 'yyyy.MM.dd')}
              </LabelXS>
            </BaseBlock>
          </Link>
        )
      })}
      {inquiries.length > 0 ? (
        <Pagination
          {...paginationProps}
          className={css({ marginTop: '32px' })}
        />
      ) : (
        <EmptyList title="1대1 문의 내역이 없습니다." padding="120px" />
      )}
    </BaseBlock>
  )
}

InquiriesSettings.getLayout = (page) => (
  <SettingsLayout helmetTitle="1대1 문의 내역">{page}</SettingsLayout>
)

export default InquiriesSettings
